/**
 * Alarm label, chip color, and relay-face resolution — shared by the alarm
 * history grid and the active-alarms dashboard summary card.
 *
 * Every alarm row on either surface carries only an `alarm: {type,id,name}`
 * ref (no denormalized catalog data on the wire); these functions resolve
 * that ref, joined against the alarm-definition/category reference catalog,
 * into the label, severity chip color, and relay-load ("face") names a user
 * actually reads.
 *
 * Contains:
 * - translateAlarm, translateAlarmDescription — alarm-key i18n lookups with a
 *   catalog-derived fallback
 * - alarmChipFamily — severity -> status-palette family
 * - facesForRelayNumber — relay_number -> relay-load display names
 * - timestampMs — guarded ms conversion for date-ish values
 *
 * Returns plain JS — no JSX.
 */
import { severityStatusFamily } from '@sophocles/utils/alarmSeverity';

/**
 * @param {Function} tAlarm   - alarm-namespace translator (useAlarmTranslation())
 * @param {string}   key      - alarm definition key
 * @param {string}   fallback - display text when no translation exists for the key
 * @returns {string}
 */
export function translateAlarm(tAlarm, key, fallback) {
  if (!key) return fallback || '';
  return tAlarm(`alarms.${key}`, { defaultValue: fallback || key });
}

/**
 * @param {Function} tAlarm
 * @param {object}   def      - alarm definition from the reference catalog (may be {})
 * @param {object}   category - resolved top-level category (may be null)
 * @returns {string}
 */
export function translateAlarmDescription(tAlarm, def, category) {
  const fallback = category?.description || '';
  if (def?.key) {
    const description = tAlarm(`alarms.${def.key}_description`, { defaultValue: fallback });
    if (description) return description;
  }
  if (category?.key) {
    return tAlarm(`alarms.cat_${category.key}_description`, { defaultValue: fallback });
  }
  return fallback;
}

/**
 * Resolve the status-palette family for an alarm row. Severity is guaranteed
 * on every alarm definition (BE default: warning); an absent severity means
 * the catalog lookup missed, and renders neutral so a stale catalog can't
 * masquerade as a real severity.
 *
 * @param {string} [severity]
 * @returns {string}
 */
export function alarmChipFamily(severity) {
  return severity ? severityStatusFamily(severity) : 'neutral';
}

function fallbackRelayLabel(discriminator, t) {
  return `${t('measurements.relay', 'Relay')} ${discriminator}`;
}

function loadFallbackLabel(index, loadTermSingular) {
  return `${loadTermSingular} ${index + 1}`;
}

/**
 * @param {number|null} relayNumber      - alarm definition's relay_number, or null
 * @param {object[]}    relays           - plain JS relay array from unit.relays
 * @param {Function}    t
 * @param {string}      loadTermSingular - customer-type relay-load term ("Panel"/"Zone"/"Face")
 * @returns {string} the relay's load names, joined; a fallback "Relay N" label when
 *   the relay isn't found or has no named loads; '' when relayNumber is null
 */
export function facesForRelayNumber(relayNumber, relays, t, loadTermSingular) {
  if (relayNumber == null) return '';
  const relay = relays.find(r => Number(r.relay_number) === Number(relayNumber));
  if (!relay) return fallbackRelayLabel(relayNumber, t);
  const loadNames = (relay.loads ?? []).map((load, index) => {
    const name = String(load.name ?? '').trim();
    return name || loadFallbackLabel(index, loadTermSingular);
  });
  return loadNames.length > 0 ? loadNames.join(', ') : fallbackRelayLabel(relayNumber, t);
}

/**
 * @param {string|Date} value
 * @returns {number} epoch ms, or 0 for empty/invalid input
 */
export function timestampMs(value) {
  if (!value) return 0;
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}
